import { useEffect } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import landingI18n from "@/lib/i18n-landing";
import Header from "@/components/landing/Header";
import Hero from "@/components/landing/Hero";
import PainsAndSolutions from "@/components/landing/PainsAndSolutions";
import ModulesGallery from "@/components/landing/ModulesGallery";
import ForChainsTeaser from "@/components/landing/ForChainsTeaser";
import Testimonials from "@/components/landing/Testimonials";
import FAQ from "@/components/landing/FAQ";
import DemoForm from "@/components/landing/DemoForm";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/landing/SEO";
import "@/components/landing/playium-theme.css";

// Landing público de Playium. Cada sección está aislada en
// `src/components/landing/` y los textos se cargan desde
// `src/locales/landing/{es,ca,en}.json` vía la instancia i18next del archivo
// `src/lib/i18n-landing.ts` (independiente del sistema i18n del dashboard).
//
// Estructura del hub tras Bloque 4 v2 (sustituye Pillars + Problem por
// PainsAndSolutions, una única sección 3-to-3):
//   1. Header (dinámico, transparente→sólido, dropdown Funcionalidades)
//   2. Hero (split 60/40 con HeroCube en CSS 3D)
//   3. PainsAndSolutions (3 dolores en cara izq → 3 soluciones en der)
//   4. ModulesGallery (10 módulos con captura grande, alternados, link a /features#<key>)
//   5. ForChainsTeaser (banda dedicada al franquiciador → /chain)
//   6. Testimonials (con disclaimer hasta autorización)
//   7. FAQ (incluye offline / hardware / migración)
//   8. DemoForm (cierre visual + form newsletter/waitlist)
//   9. Footer (anchors + spokes + legales + selector idioma)
//
// Los packs YA NO viven aquí: se movieron a /pricing. Anunciar precios en
// la home obligaba a tocarla cada vez que cambia el catálogo, y a mantener
// una cuarta copia de los importes además de las tres que ya vigila
// src/test/billing-catalog-consistency.test.ts.
export default function Landing() {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  // Pausa las animaciones decorativas de cada sección cuando está fuera del
  // viewport. Con `will-change` permanente, las animaciones infinitas de TODAS
  // las secciones se componen a la vez aunque no se vean; dejar corriendo solo
  // la sección visible reduce mucho las capas compuestas simultáneas (clave en
  // GPUs modestas). Cero cambio visual: solo ves la sección en pantalla.
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    const sections = Array.from(
      document.querySelectorAll<HTMLElement>(".playium-landing section"),
    );
    if (sections.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          e.target.classList.toggle("is-anim-offscreen", !e.isIntersecting);
        }
      },
      { rootMargin: "150px 0px" },
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  return (
    <I18nextProvider i18n={landingI18n}>
      <LandingHead />
      <div className="playium-landing min-h-screen bg-white text-playium-navy">
        <Header />
        <Hero />
        <PainsAndSolutions />
        <ModulesGallery />
        <ForChainsTeaser />
        <Testimonials />
        <FAQ />
        <DemoForm />
        <Footer onScrollTo={scrollTo} />
      </div>
    </I18nextProvider>
  );
}

// `<SEO>` necesita estar DENTRO del I18nextProvider para leer i18n.language
// y los strings traducidos. Lo extraemos a un componente interno para no
// duplicar el provider.
function LandingHead() {
  const { t } = useTranslation("landing");
  return (
    <SEO
      title={`Playium — ${t("nav.tagline")}`}
      description={t("hero.subtitle")}
      path="/"
    />
  );
}
